import React from 'react';
import { motion } from 'framer-motion';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Mic, MicOff, Video as VideoIcon, VideoOff, Crown, MonitorUp, X, Hand } from 'lucide-react';

/**
 * ParticipantsPanel — Google Meet style "People" list.
 *
 * Shows every participant with their live mic/camera status, a "You" tag for
 * the local user and a host badge. Full-screen overlay on mobile (via the
 * `participants-panel-mobile` CSS class).
 */
const ParticipantRow = ({ participant, isHost, handRaised }) => {
    const name = participant.name || participant.identity || 'Guest';
    const micOn = participant.isMicrophoneEnabled;
    const camOn = participant.isCameraEnabled;
    const sharing = participant.isScreenShareEnabled;
    const initial = name.charAt(0).toUpperCase();

    return (
        <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${handRaised ? 'bg-amber-400/10' : 'hover:bg-white/5'}`}>
            <Avatar className="w-9 h-9 flex-shrink-0">
                <AvatarFallback className="bg-neon-cyan/20 text-neon-cyan text-sm">{initial}</AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    {handRaised && <Hand className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />}
                    <span className="text-sm text-white truncate">{name}</span>
                    {participant.isLocal && (
                        <span className="text-xs text-grey-400 flex-shrink-0">(You)</span>
                    )}
                    {isHost && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-300 bg-amber-400/15 px-1.5 py-0.5 rounded-full flex-shrink-0">
                            <Crown className="w-2.5 h-2.5" /> Host
                        </span>
                    )}
                </div>
            </div>

            {/* Status icons */}
            <div className="flex items-center gap-2 flex-shrink-0">
                {sharing && <MonitorUp className="w-4 h-4 text-neon-cyan" title="Sharing screen" />}
                {micOn
                    ? <Mic className="w-4 h-4 text-grey-300" />
                    : <MicOff className="w-4 h-4 text-red-400" />}
                {camOn
                    ? <VideoIcon className="w-4 h-4 text-grey-300" />
                    : <VideoOff className="w-4 h-4 text-grey-500" />}
            </div>
        </div>
    );
};

const ParticipantsPanel = ({ participants = [], hostId, raisedHands = {}, onClose }) => {
    const hostPrefix = hostId ? String(hostId).slice(0, 8) : null;

    // Raised hands first (in the order they were raised), then everyone else.
    const sortedParticipants = [...participants].sort((a, b) => {
        const aRaised = raisedHands[a.identity]?.raisedAt ?? Infinity;
        const bRaised = raisedHands[b.identity]?.raisedAt ?? Infinity;
        return aRaised - bRaised;
    });

    return (
        <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="participants-panel-mobile md:w-[320px] h-full max-h-[calc(100dvh-140px)] bg-void-paper border-l border-white/5 flex flex-col overflow-hidden z-20"
        >
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <h3 className="font-heading font-semibold text-white">
                    People <span className="text-grey-400 font-normal">({participants.length})</span>
                </h3>
                <button onClick={onClose} className="text-grey-400 hover:text-white" aria-label="Close people panel">
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="flex-1 min-h-0">
                <ScrollArea className="h-full p-2">
                    {participants.length === 0 ? (
                        <p className="text-center text-grey-400 text-sm py-8">No one here yet</p>
                    ) : (
                        <div className="space-y-0.5">
                            {sortedParticipants.map((p) => (
                                <ParticipantRow
                                    key={p.sid || p.identity}
                                    participant={p}
                                    isHost={hostPrefix && p.identity === hostPrefix}
                                    handRaised={Boolean(raisedHands[p.identity])}
                                />
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </div>
        </motion.div>
    );
};

export default ParticipantsPanel;
